/*
 * Magnum PDP sticky add-to-cart bar.
 *
 * Design rule for this whole file: the bar MIRRORS the live DOM, it never
 * recomputes business logic. Price is cloned from #price-{section}, so market
 * currency, subscription pricing and volume rules stay correct for free.
 * Availability is copied from the native submit button. And the bar's own
 * button does nothing but forward a click to that native button, so the cart
 * drawer, the Klaviyo add-to-cart tracker (which reads form.elements),
 * the checkout-button spinner, quantity and selling plans all keep working
 * exactly as they do without this bar.
 */
if (!customElements.get('magnum-sticky-atc')) {
  customElements.define(
    'magnum-sticky-atc',
    class MagnumStickyAtc extends HTMLElement {
      connectedCallback() {
        // 1. Re-entry guard. Re-parenting the node to <body> below re-fires
        //    connectedCallback; so does any later move.
        if (this.dataset.mounted) return;

        // 2. snippets/card-product.liquid renders main-product inside the
        //    quick-add modals. A second bar must never appear there.
        if (this.closest('quick-add-modal, .quick-add-modal')) {
          this.remove();
          return;
        }

        // 3. Singleton, newest wins. This is what keeps theme-editor section
        //    reloads clean: the fresh bar evicts the stale one.
        document.querySelectorAll('magnum-sticky-atc[data-mounted]').forEach((other) => {
          if (other !== this) other.remove();
        });

        this.sectionId = this.dataset.sectionId;
        this.productInfo = this.closest('product-info');
        this.nativeButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

        // 4. Fail to NO bar rather than to a broken bar.
        if (!this.nativeButton || !this.productInfo) {
          this.remove();
          return;
        }

        this.imageEl = this.querySelector('[data-sticky-image]');
        this.variantEl = this.querySelector('[data-sticky-variant]');
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.buttonEl = this.querySelector('[data-sticky-button]');
        this.buttonTextEl = this.querySelector('[data-sticky-button-text]');
        this.spinnerEl = this.querySelector('.loading__spinner');
        this.initialImageSrc = this.imageEl ? this.imageEl.getAttribute('src') : null;

        this.variants = this.parseVariants();
        this.hideVariantLine =
          this.variants.length <= 1 || (this.variants.length === 1 && this.variants[0].title === 'Default Title');

        this.syncQueued = false;
        this.teardownCalled = false;

        this.onClick = this.onClick.bind(this);
        this.onDomChange = this.onDomChange.bind(this);
        this.updateVisibility = this.updateVisibility.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onSectionUnload = this.onSectionUnload.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
        this.runSync = this.runSync.bind(this);

        this.dataset.mounted = 'true';

        // Mandatory: a transformed / filtered / contained ancestor would turn
        // position:fixed into position:absolute relative to that ancestor and
        // strand the bar mid-page. Only <body> is guaranteed safe.
        document.body.appendChild(this);

        this.bind();
        this.requestSync();
      }

      parseVariants() {
        const node = this.querySelector('[data-sticky-variants]');
        if (!node) return [];
        try {
          const parsed = JSON.parse(node.textContent);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      }

      bind() {
        if (this.buttonEl) this.buttonEl.addEventListener('click', this.onClick);

        // Catch-all: product-info.js swaps innerHTML on variant change and
        // toggles the submit button's disabled attribute.
        this.mutationObserver = new MutationObserver(this.onDomChange);
        this.mutationObserver.observe(this.productInfo, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['disabled'],
        });

        // updateVariantInputs() dispatches a bubbling change on the hidden input.
        this.productInfo.addEventListener('change', this.onDomChange);

        // The submit lifecycle lives on the native button as `class="loading"` +
        // aria-disabled (product-form.js sets both on submit and clears them in
        // its finally block). The subtree observer above filters to `disabled`
        // only -- widening it to `class` across all of product-info would fire on
        // every slider tick. One narrow observer on the button is cheap and exact.
        this.buttonStateObserver = new MutationObserver(this.onDomChange);
        this.buttonStateObserver.observe(this.nativeButton, {
          attributes: true,
          attributeFilter: ['class', 'aria-disabled', 'disabled'],
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.onDomChange);
        }

        window.addEventListener('pageshow', this.onPageShow);
        document.addEventListener('shopify:section:unload', this.onSectionUnload);

        // Visibility: show only once the native button has been scrolled PAST.
        // rect.bottom < 0 is what distinguishes "above the viewport" from
        // "still below the fold", where the bar must stay hidden.
        //
        // An IntersectionObserver ALONE is not enough here. With threshold 0 it
        // only fires when isIntersecting flips. A scroll that jumps the button
        // straight from below the fold to above the viewport (anchor link,
        // restored scroll position, a fling on mobile) never intersects at all,
        // so no entry is delivered and the bar would stay hidden forever.
        // Reproduced on a 375px viewport: button bottom at -548 with no bar.
        // So the rAF-throttled scroll listener is the authority, and the
        // observer stays on to catch the button MOVING without a scroll
        // (variant swap, lazy image, accordion) which scroll alone would miss.
        this.intersectionObserver = new IntersectionObserver(this.updateVisibility, { threshold: 0 });
        this.intersectionObserver.observe(this.nativeButton);

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });

        // Publish the bar's height as --magnum-sticky-atc-h so the stylesheet can
        // lift the third-party accessibility widget clear of it. Measured rather
        // than hard-coded: the bar's height changes with viewport and font scale.
        if (typeof ResizeObserver === 'function') {
          this.resizeObserver = new ResizeObserver(() => this.publishHeight());
          this.resizeObserver.observe(this);
        }
        this.publishHeight();

        this.updateVisibility();
      }

      publishHeight() {
        const height = Math.round(this.getBoundingClientRect().height);
        if (!height || this.lastHeight === height) return;
        this.lastHeight = height;
        document.documentElement.style.setProperty('--magnum-sticky-atc-h', `${height}px`);
      }

      /*
       * Which control is actually on the page right now.
       *
       * On a sold-out variant Klaviyo's app embed sets the native ATC button to
       * display:none (it stays in the DOM, disabled, reading "Sold out") and
       * injects #klaviyo-bis-button-container with a back-in-stock button. A
       * display:none button reports rect.bottom === 0, so anchoring on it would
       * make `bottom < 0` false forever and the bar would never appear.
       *
       * Returns the element to ANCHOR visibility on and the element to CLICK --
       * they differ for BIS: the real button is a GRANDCHILD inside a
       * hashed-class wrapper div, so `> button` matches nothing.
       */
      buyControl() {
        const bis = document.getElementById('klaviyo-bis-button-container');
        if (bis && bis.isConnected) {
          const rect = bis.getBoundingClientRect();
          if (getComputedStyle(bis).display !== 'none' && rect.height > 0) {
            const target = bis.querySelector('button, a');
            if (target) return { anchor: bis, clickTarget: target, mode: 'bis' };
          }
        }
        const nat = document.getElementById(`ProductSubmitButton-${this.sectionId}`) || this.nativeButton;
        if (nat && nat.isConnected) return { anchor: nat, clickTarget: nat, mode: 'atc' };
        return null;
      }

      // Keep the IntersectionObserver pointed at whichever control is live, so a
      // lazy image or accordion that shifts it without a scroll still registers.
      observeAnchor(el) {
        if (!el || this.anchorEl === el || !this.intersectionObserver) return;
        this.anchorEl = el;
        this.intersectionObserver.disconnect();
        this.intersectionObserver.observe(el);
      }

      updateVisibility() {
        const ctl = this.buyControl();
        if (!ctl) return;
        this.observeAnchor(ctl.anchor);
        this.setVisible(ctl.anchor.getBoundingClientRect().bottom < 0);
      }

      onScroll() {
        if (this.visQueued) return;
        this.visQueued = true;
        requestAnimationFrame(() => {
          this.visQueued = false;
          this.updateVisibility();
        });
      }

      setVisible(visible) {
        if (this.isVisible === visible) return;
        this.isVisible = visible;
        this.classList.toggle('is-visible', visible);
        // Keep the bar out of the tab order and the a11y tree while hidden.
        this.setAttribute('aria-hidden', visible ? 'false' : 'true');
        this.toggleAttribute('inert', !visible);
        // Hook for the stylesheet to move the accessibility widget out of the
        // bar's way. On <html> so the rule needs no :has() support.
        document.documentElement.classList.toggle('magnum-sticky-atc-visible', visible);
      }

      onDomChange() {
        this.requestSync();
      }

      onPageShow() {
        this.requestSync();
        this.updateVisibility();
      }

      onSectionUnload(event) {
        if (event.detail && String(event.detail.sectionId) === String(this.sectionId)) this.remove();
      }

      requestSync() {
        if (this.syncQueued) return;
        this.syncQueued = true;
        requestAnimationFrame(this.runSync);
      }

      currentVariantId() {
        const form = document.getElementById(`product-form-${this.sectionId}`);
        // Scope to the add-to-cart form: the installment form carries its own
        // input[name="id"] and would otherwise win the lookup.
        const input =
          (form && (form.querySelector('input[name="id"]') || form.querySelector('.product-variant-id'))) ||
          this.productInfo.querySelector('.product-variant-id');
        return input ? String(input.value) : null;
      }

      fallbackImageSrc() {
        const items = Array.from(this.productInfo.querySelectorAll('.product__media-item')).filter(
          (item) => getComputedStyle(item).display !== 'none'
        );
        const active = items.find((item) => item.classList.contains('is-active')) || items[0];
        const img = active ? active.querySelector('img') : null;
        return img ? img.getAttribute('src') : null;
      }

      runSync() {
        this.syncQueued = false;
        if (!this.isConnected || !this.productInfo.isConnected) return;

        const variantId = this.currentVariantId();
        const variant = this.variants.find((item) => String(item.id) === variantId) || null;

        // Variant label.
        if (this.variantEl) {
          if (this.hideVariantLine || !variant || variant.title === 'Default Title') {
            this.variantEl.hidden = true;
          } else {
            this.variantEl.hidden = false;
            if (this.variantEl.textContent !== variant.title) this.variantEl.textContent = variant.title;
          }
        }

        // Image.
        if (this.imageEl) {
          const src = (variant && variant.image) || this.fallbackImageSrc() || this.initialImageSrc;
          if (src && this.imageEl.getAttribute('src') !== src) this.imageEl.setAttribute('src', src);
        }

        // Price: clone the live node. snippets/price.liquid renders no id
        // attributes, so this cannot duplicate an id into the document.
        if (this.priceEl) {
          const livePrice = document.getElementById(`price-${this.sectionId}`);
          if (livePrice) {
            if (this.priceEl.innerHTML !== livePrice.innerHTML) {
              this.priceEl.innerHTML = livePrice.innerHTML;
            }
            // Mirror visibility, not just content. On an invalid option combo
            // setUnavailable() (product-info.js:236) only adds .hidden to
            // #price-{section} and leaves the old markup in place, so cloning
            // innerHTML alone would show the previous variant's price next to
            // a disabled "Unavailable" button.
            this.priceEl.classList.toggle('hidden', livePrice.classList.contains('hidden'));
          }
        }

        // Availability + label: mirror whichever control is live.
        if (this.buttonEl) {
          const ctl = this.buyControl();
          const mode = ctl ? ctl.mode : 'atc';
          this.dataset.mode = mode;

          if (mode === 'bis') {
            // Back-in-stock: the bar becomes a "Notify me" CTA. Klaviyo owns the
            // label (it is edited in their dashboard, not the theme), so read it
            // off the live button rather than hard-coding a string here.
            const label = (ctl.clickTarget.textContent || '').trim();
            if (this.buttonTextEl && label && this.buttonTextEl.textContent !== label) {
              this.buttonTextEl.textContent = label;
            }
            // Always actionable, and never a spinner: this opens Klaviyo's form,
            // it does not POST to the cart.
            this.buttonEl.removeAttribute('disabled');
            this.buttonEl.removeAttribute('aria-disabled');
            this.buttonEl.classList.remove('loading');
            if (this.spinnerEl) this.spinnerEl.classList.add('hidden');
          } else {
            const disabled = this.nativeButton.hasAttribute('disabled');
            if (this.buttonEl.hasAttribute('disabled') !== disabled) {
              this.buttonEl.toggleAttribute('disabled', disabled);
            }
            const nativeText = this.nativeButton.querySelector('span');
            if (this.buttonTextEl && nativeText && this.buttonTextEl.textContent !== nativeText.textContent) {
              this.buttonTextEl.textContent = nativeText.textContent;
            }

            // Loading state, mirrored rather than timed: it follows the real
            // request, so it clears exactly when the add finishes (or errors) and
            // can never strand a spinner on a failed fetch. Reuses Dawn's own
            // `.button.loading` CSS, which hides the label and centres the spinner.
            const loading = this.nativeButton.classList.contains('loading');
            this.buttonEl.classList.toggle('loading', loading);
            if (loading) {
              this.buttonEl.setAttribute('aria-disabled', 'true');
            } else {
              this.buttonEl.removeAttribute('aria-disabled');
            }
            if (this.spinnerEl) this.spinnerEl.classList.toggle('hidden', !loading);
          }
        }

        // The control can swap between ATC and BIS without a scroll (variant
        // change, or Klaviyo injecting late), so re-evaluate visibility here too.
        this.updateVisibility();
      }

      onClick(event) {
        event.preventDefault();
        // Proxy only. Never reimplement add-to-cart here.
        const ctl = this.buyControl();
        if (!ctl) return;

        // Back-in-stock: forward to Klaviyo's own button so its form opens and
        // its tracking fires, exactly as if the shopper had clicked it directly.
        if (ctl.mode === 'bis') {
          ctl.clickTarget.click();
          return;
        }

        const btn = ctl.clickTarget;
        if (btn.hasAttribute('disabled')) return;
        // A submit is already in flight. product-form.js guards this too, but
        // returning here keeps the second click from doing anything at all.
        if (btn.getAttribute('aria-disabled') === 'true') return;
        btn.click();
      }

      disconnectedCallback() {
        // The appendChild to <body> above disconnects and reconnects this node
        // synchronously. Only tear down if it is still detached afterwards.
        Promise.resolve().then(() => {
          if (this.isConnected || this.teardownCalled) return;
          this.teardownCalled = true;
          // Leave no trace: the widget must fall back to its own position.
          // But only if no OTHER bar is mounted. On a theme-editor section
          // reload the incoming bar evicts this one and then sets both of
          // these; this teardown runs a microtask later and would wipe the
          // live bar's state, which its own early-returns never restore.
          if (!document.querySelector('magnum-sticky-atc[data-mounted]')) {
            document.documentElement.classList.remove('magnum-sticky-atc-visible');
            document.documentElement.style.removeProperty('--magnum-sticky-atc-h');
          }
          if (this.resizeObserver) this.resizeObserver.disconnect();
          if (this.intersectionObserver) this.intersectionObserver.disconnect();
          if (this.mutationObserver) this.mutationObserver.disconnect();
          if (this.buttonStateObserver) this.buttonStateObserver.disconnect();
          if (this.productInfo) this.productInfo.removeEventListener('change', this.onDomChange);
          if (this.buttonEl) this.buttonEl.removeEventListener('click', this.onClick);
          if (typeof this.unsubscribeVariantChange === 'function') this.unsubscribeVariantChange();
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
          window.removeEventListener('pageshow', this.onPageShow);
          document.removeEventListener('shopify:section:unload', this.onSectionUnload);
        });
      }
    }
  );
}
